// Homepage presets manager — list, apply, rename, delete, and save the
// current live home sections as a new preset. Rendered inside the Home
// PageSectionsPanel.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Save, Play, Pencil, Trash2, Layers, PencilLine, Copy } from "lucide-react";
import { SUBTYPE_META } from "@/components/sectionTypes";
import PageSectionsEditor from "@/components/PageSectionsEditor";

export default function HomePresetsPanel({ liveSections, onApplied }) {
  const [presets, setPresets] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [renameDesc, setRenameDesc] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editSections, setEditSections] = useState({ open: false, preset: null });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/home-presets").then(r => setPresets(r.data)).catch(() => setPresets([]));
  useEffect(() => { load(); }, []);

  const doApply = async (p) => {
    if (!window.confirm(`Load "${p.name}"? This replaces the current home layout with the preset. Save your current layout as a preset first if you want to keep it.`)) return;
    setBusy(true);
    try {
      await api.post(`/home-presets/${p.preset_id}/apply`);
      toast.success(`Loaded "${p.name}"`);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  };

  const doSave = async () => {
    if (!saveName.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post("/home-presets", { name: saveName, description: saveDesc, sections: liveSections });
      toast.success(`Saved preset "${saveName}"`);
      setSaveOpen(false); setSaveName(""); setSaveDesc("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const doRename = async () => {
    if (!renameName.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.put(`/home-presets/${renameId}`, { name: renameName, description: renameDesc });
      toast.success("Updated");
      setRenameId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const doDelete = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/home-presets/${id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const savePresetSections = async (nextSections) => {
    if (!editSections.preset) return;
    await api.put(`/home-presets/${editSections.preset.preset_id}`, { sections: nextSections });
    toast.success(`Updated "${editSections.preset.name}"`);
    load();
  };

  const doDuplicate = async (p) => {
    setBusy(true);
    try {
      // Pick a name that doesn't collide with an existing preset ("X (copy)",
      // then "X (copy 2)", "(copy 3)", etc.).
      const base = `${p.name} (copy)`;
      const existing = new Set((presets || []).map(x => x.name));
      let name = base, n = 2;
      while (existing.has(name)) { name = `${p.name} (copy ${n++})`; }
      await api.post("/home-presets", {
        name,
        description: p.description || "",
        sections: p.sections || [],
      });
      toast.success(`Duplicated as "${name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    } finally { setBusy(false); }
  };

  if (presets === null) return null;

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-sky-700"/>
          <div className="text-sm font-semibold text-slate-900">Home page presets</div>
          <span className="text-xs text-slate-500">({presets.length})</span>
        </div>
        <Button data-testid="preset-save-current" size="sm" variant="outline"
                onClick={() => setSaveOpen(true)}>
          <Save className="w-3.5 h-3.5 mr-1.5"/>Save current as preset…
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Snapshot the current live home page as a named layout you can switch back to later — great for seasonal variants (Halloween, Winter Care, Grief Awareness Month…).
      </p>

      {presets.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500 italic text-center py-4 border border-dashed border-slate-300 rounded-md">
          No presets yet. Save the current layout above to create your first one.
        </div>
      ) : (
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {presets.map(p => {
            const bySubtype = (p.sections || []).reduce((a, s) => { const k = s.subtype || "prose"; a[k] = (a[k]||0)+1; return a; }, {});
            return (
              <div key={p.preset_id} data-testid={`preset-card-${p.preset_id}`}
                   className="bg-white border border-slate-200 rounded-md p-3 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                    {p.description && <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{p.description}</div>}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  {p.sections?.length || 0} sections
                  {Object.keys(bySubtype).length > 0 && (
                    <> · {Object.entries(bySubtype).slice(0,3).map(([k,n]) => `${n} ${(SUBTYPE_META[k]?.label || k).toLowerCase()}`).join(", ")}</>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Button data-testid={`preset-apply-${p.preset_id}`} size="sm"
                          className="bg-sky-700 hover:bg-sky-600 h-7 px-2"
                          onClick={() => doApply(p)} disabled={busy}>
                    <Play className="w-3 h-3 mr-1"/>Load
                  </Button>
                  <button data-testid={`preset-edit-${p.preset_id}`}
                          className="text-xs text-slate-500 hover:text-sky-700 inline-flex items-center gap-1"
                          onClick={() => setEditSections({ open: true, preset: p })}>
                    <PencilLine className="w-3 h-3"/>Edit
                  </button>
                  <button data-testid={`preset-duplicate-${p.preset_id}`}
                          className="text-xs text-slate-500 hover:text-sky-700 inline-flex items-center gap-1"
                          onClick={() => doDuplicate(p)} disabled={busy}>
                    <Copy className="w-3 h-3"/>Duplicate
                  </button>
                  <button data-testid={`preset-rename-${p.preset_id}`}
                          className="text-xs text-slate-500 hover:text-sky-700 inline-flex items-center gap-1"
                          onClick={() => { setRenameId(p.preset_id); setRenameName(p.name); setRenameDesc(p.description || ""); }}>
                    <Pencil className="w-3 h-3"/>Rename
                  </button>
                  <button data-testid={`preset-delete-${p.preset_id}`}
                          className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1 ml-auto"
                          onClick={() => setConfirmDelete(p)}>
                    <Trash2 className="w-3 h-3"/>Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Save current-as-preset dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Save current home page as preset</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Name</label>
              <Input data-testid="preset-save-name" value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="e.g. Grief Awareness Month"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Description (optional)</label>
              <Textarea rows={2} value={saveDesc} onChange={e => setSaveDesc(e.target.value)} placeholder="Short note describing this layout."/>
            </div>
            <div className="text-xs text-slate-500">
              Snapshotting <strong>{liveSections?.length || 0}</strong> section{(liveSections?.length||0)===1?"":"s"} from the current live home page.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button data-testid="preset-save-confirm" className="bg-sky-700 hover:bg-sky-600" onClick={doSave} disabled={busy}>Save preset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameId} onOpenChange={v => !v && setRenameId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Rename preset</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Name</label>
              <Input value={renameName} onChange={e => setRenameName(e.target.value)}/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Description</label>
              <Textarea rows={2} value={renameDesc} onChange={e => setRenameDesc(e.target.value)}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameId(null)}>Cancel</Button>
            <Button className="bg-sky-700 hover:bg-sky-600" onClick={doRename} disabled={busy}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={v => !v && setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete preset?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">
            "{confirmDelete?.name}" will be removed. This doesn't affect the current live home page.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={() => doDelete(confirmDelete.preset_id)} disabled={busy}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit sections dialog — reuses the same PageSectionsEditor with a lock
         key so two admins can't edit the same preset at once. */}
      <PageSectionsEditor
        open={editSections.open}
        onOpenChange={(v) => setEditSections(prev => ({ ...prev, open: v }))}
        title={editSections.preset ? `Edit preset — ${editSections.preset.name}` : "Edit preset"}
        resourceKey={editSections.preset ? `home_preset:${editSections.preset.preset_id}` : null}
        initialSections={editSections.preset?.sections || []}
        onSave={savePresetSections}
      />
    </div>
  );
}
